import React, { useEffect, useRef, useState, useLayoutEffect } from 'react'
import moment from 'moment'
import { ActionSheet, Dialog } from 'antd-mobile'
import { FaEye } from 'react-icons/fa'
import IosSpinner from './IosSpinner'
import BotMessage from './BotMessage'
import { extractDocumentsFromText } from '../../utils/common'
import { BOT_USER_ID, BOT_USER_NAME } from '../../utils/constant'
import { getUserAvatar, hasAvatarImage } from '../../utils/avatarUtils'
import './styles/ChannelMessages.css'

const ChannelMessages = ({
  messages,
  unreadCount,
  onReplyClick,
  onDeleteMessage,
  onEditMessage,
  onPinMessage,
  currentUserId,
  threads = {},
  editingMessageId,
  jumpToMessageId,
  loadingMore = false,
  users = [],
  userAvatars = [],
  triggerScroll,
  clientId,
  feedbacks = []
}) => {
  const bottomRef = useRef(null)
  const prevMessagesRef = useRef([])
  const initialDividerIndexRef = useRef(null)
  const [longPressTimer, setLongPressTimer] = useState(null)
  const [activeMsgId, setActiveMsgId] = useState(null)
  const messageRefs = useRef({})
  const firstUnreadMessageIdRef = useRef(null)

  const filteredMessages = messages.filter(
    (msg) => msg.t !== 'message_pinned' && msg.t !== 'au'
  )

  if (firstUnreadMessageIdRef.current === null && unreadCount > 0) {
    const targetIndex =
      filteredMessages.length >= unreadCount
        ? filteredMessages.length - unreadCount
        : null

    if (targetIndex !== null) {
      firstUnreadMessageIdRef.current = filteredMessages[targetIndex]?._id || null
    }
  }

  const dividerIndex = firstUnreadMessageIdRef.current
    ? filteredMessages.findIndex(msg => msg._id === firstUnreadMessageIdRef.current)
    : null

  useEffect(() => {
    const prevMessages = prevMessagesRef.current || []
    const lastPrevId = prevMessages[prevMessages.length - 1]?._id
    const lastCurrId = messages?.[messages.length - 1]?._id

    if (lastCurrId && lastCurrId !== lastPrevId) {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' })
    }

    prevMessagesRef.current = messages || []
  }, [messages])

  useLayoutEffect(() => {
    if (jumpToMessageId) {
      const scrollToMessage = (retryCount = 0) => {
        const targetEl = messageRefs.current[jumpToMessageId]

        if (targetEl) {
          requestAnimationFrame(() => {
            try {
              targetEl.scrollIntoView({
                behavior: 'instant',
                block: 'center'
              })

              targetEl.classList.add('jump-highlight')

              setTimeout(() => {
                targetEl?.classList.remove('jump-highlight')
              }, 1500)
            } catch (err) {
              console.error('[JumpEffect] scrollIntoView error:', err)
            }
          })
        } else if (retryCount < 5) {
          // Retry after a delay if element not found
          setTimeout(() => scrollToMessage(retryCount + 1), 100 * (retryCount + 1))
        } else {
          console.warn('[JumpEffect] Failed to find element after retries:', jumpToMessageId)
        }
      }

      scrollToMessage()
    }
  }, [jumpToMessageId, messages]) 

  const startLongPress = (msg) => {
    setActiveMsgId(msg._id)
    const timer = setTimeout(() => {
      showActions(msg)
    }, 600)
    setLongPressTimer(timer)
  }

  const cancelLongPress = () => {
    if (longPressTimer) clearTimeout(longPressTimer)
    setActiveMsgId(null)
  }

  const showActions = (msg) => {
    const hasThreadReplies = threads[msg._id]?.length > 0 || msg.tcount > 0
    const handler = ActionSheet.show({
      actions: [
        ...(msg.u.username !== BOT_USER_ID ? [{
          text: 'Reply in Thread',
          key: 'reply',
          onClick: () => {
            onReplyClick && onReplyClick(msg)
            handler.close()
          }
        }] : []),
        ...(msg.u?._id === currentUserId
          ? [
            {
              text: 'Edit Message',
              key: 'edit',
              onClick: () => {
                onEditMessage && onEditMessage(msg)
                handler.close()
              }
            },
            ...(!hasThreadReplies
              ? [{
                text: 'Delete Message',
                key: 'delete',
                danger: true,
                onClick: async () => {
                  handler.close()
                  const result = await Dialog.confirm({
                    title: 'Delete Message',
                    content: 'Do you want to delete this message?',
                    cancelText: 'Cancel',
                    confirmText: 'Delete'
                  })
                  if (result) {
                    onDeleteMessage &&
                      onDeleteMessage({ messageId: msg._id, roomId: msg.rid })
                  }
                }
              }]
              : [])
          ]
          : []),
        ...(!msg.pinned
          ? [
            {
              text: 'Pin to Conversation',
              key: 'pin',
              onClick: () => {
                onPinMessage && onPinMessage(msg, false)
                handler.close()
              }
            }
          ]
          : [])
      ],
      cancelText: 'Cancel',
      onClose: () => setActiveMsgId(null)
    })
  }

  useLayoutEffect(() => {
    const messageContents = document.querySelectorAll('.message-content')
    messageContents.forEach(content => {
      const imgs = content.querySelectorAll('img')
      imgs.forEach(img => {
        img.oncontextmenu = (e) => e.preventDefault()
        img.ondragstart = (e) => e.preventDefault()
      })
    })
    // Cleanup
    return () => {
      messageContents.forEach(content => {
        const imgs = content.querySelectorAll('img')
        imgs.forEach(img => {
          img.oncontextmenu = null
          img.ontouchstart = null
          img.ondragstart = null
        })
      })
    }
  }, [messages])

  return (
    <div className="channel-messages-wrapper">
      {loadingMore && (
        <div className="loading-more-messages">
          <IosSpinner size={24} />
        </div>
      )}
    {filteredMessages.map((msg, index) => {
      let text = msg.msg
      const { documents, text: cleanedText } = extractDocumentsFromText(text)
      const isNewFormat = cleanedText !== text
      text = cleanedText
      const fullBotResponse = msg.msg

      if (documents.length === 0 && msg.mentions) {
        msg.mentions.forEach((mention) => {
          text = text.replaceAll(
            `@${mention.username}`,
            `<span class='mention'>@${mention.name}</span>`
          )
        })
      }

      let sender = msg.u.name
      if (!sender) {
          const sender_user = users.filter((user) => user._id === msg.u._id)
          sender = sender_user.length > 0 ? sender_user[0].name : sender
      }
      if (msg.u.username === BOT_USER_ID && !msg.alias) return <></>

      if (msg.u.username === BOT_USER_ID) {
        sender = BOT_USER_NAME
      }

      return (
        <React.Fragment key={msg._id}>
          {dividerIndex !== null && index === dividerIndex && (
            <>
              <div className="unread-divider">Unread Messages</div>
            </>
          )}

          <div
            ref={el => messageRefs.current[msg._id] = el}
            className={`message-container
              ${msg.pinned ? 'pinned-message' : ''}
              ${activeMsgId === msg._id ? 'long-press-active' : ''}
              ${editingMessageId === msg._id ? 'editing-message' : ''}
              ${jumpToMessageId === msg._id ? 'editing-message' : ''}`
            }
            onMouseDown={() => startLongPress(msg)}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onTouchStart={() => startLongPress(msg)}
            onTouchEnd={cancelLongPress}
            onContextMenu={(e) => e.preventDefault()}
          >
            {msg.pinned && (
              <div className="pinned-label">
                <span className="pin-icon">📌</span>
                Pinned
              </div>
            )}

            <div className="message-header">
              <div className="message-avatar">
                {hasAvatarImage(msg.u?.username, userAvatars) ? (
                  <img
                    src={getUserAvatar(msg.u?.username, userAvatars)}
                    alt={msg.u?.username || 'User'}
                    className="avatar-image"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                <div className={`avatar-fallback ${hasAvatarImage(msg.u?.username, userAvatars) ? 'hidden' : ''}`}>
                  {getUserAvatar(msg.u?.username, userAvatars)}
                </div>
              </div>
              <div className="message-header-content">
                <span className="message-sender">
                  {sender}
                </span>
                <span className="message-timestamp">
                  {moment(msg.ts).format('MMM DD, YYYY h:mm A')}
                </span>
              </div>
            </div>

            <div className={`message-box${msg.u?._id === currentUserId ? ' my-message-box' : ''}`}>
              {documents.length > 0 ? (
                <BotMessage
                  documents={documents}
                  explanation={isNewFormat ? text : undefined}
                  messageId={msg._id}
                  botResponse={fullBotResponse}
                  query={index > 0 ? filteredMessages[index - 1]?.msg : ''}
                  role={1}
                  personId={clientId}
                  roomId={msg.rid}
                  feedbacks={feedbacks}
                />
              ) : (
                <div className="message-content" dangerouslySetInnerHTML={{ __html: text }} />
              )}

              {(threads[msg._id]?.length > 0 || msg.tcount > 0) && (
                <>
                  <div className="message-reply-divider" />
                  <div
                    className="reply-text"
                    onClick={() => onReplyClick(msg)}
                    title="View thread replies"
                  >
                    <FaEye size={14} style={{ marginRight: 6 }} />
                    View{' '}
                    {threads[msg._id]?.length || msg.tcount}{' '}
                    {(threads[msg._id]?.length || msg.tcount) === 1
                      ? 'reply'
                      : 'replies'}
                  </div>
                </>
              )}
            </div>
          </div>
        </React.Fragment>
      )
    })}
      <div ref={bottomRef} />
    </div>
  )
}

export default ChannelMessages
